import { useState, useEffect } from 'react';

export const useMobileDetection = () => {
  const [deviceInfo, setDeviceInfo] = useState({
    isMobile: false,
    isTablet: false,
    isDesktop: true,
    os: 'Unknown',
    osVersion: null,
    browser: 'Unknown',
    browserVersion: null,
    deviceType: 'desktop',
    deviceVendor: 'Unknown',
    deviceModel: 'Unknown',
    isTouchDevice: false,
    language: 'en',
    timezone: 'UTC',
  });

  useEffect(() => {
    const detectDevice = () => {
      const userAgent = navigator.userAgent.toLowerCase();
      const platform = navigator.platform.toLowerCase();

      // Detección de tipo de dispositivo
      const isMobile =
        /mobi|android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(
          userAgent,
        );
      const isTablet =
        /ipad|tablet|android(?!.*mobile)|kindle|silk/i.test(userAgent) ||
        (navigator.maxTouchPoints &&
          navigator.maxTouchPoints > 2 &&
          /MacIntel/.test(platform));

      // Detección de Sistema Operativo (Global)
      let os = 'Unknown';
      let osVersion = null;

      // Windows (todas las versiones)
      if (/windows/.test(userAgent)) {
        os = 'Windows';
        const winVersions = {
          'windows nt 10': '10/11',
          'windows nt 6.3': '8.1',
          'windows nt 6.2': '8',
          'windows nt 6.1': '7',
          'windows nt 6.0': 'Vista',
          'windows nt 5.2': 'Server 2003',
          'windows nt 5.1': 'XP',
          'windows nt 5.0': '2000',
          'windows nt 4.0': 'NT 4.0',
          'windows 98': '98',
          'windows 95': '95',
        };
        for (const [key, value] of Object.entries(winVersions)) {
          if (userAgent.includes(key)) {
            osVersion = value;
            break;
          }
        }
      }
      // macOS
      else if (/macintosh|mac os x|macos/.test(userAgent)) {
        os = 'macOS';
        const match = userAgent.match(/mac os x ([0-9_]+)|macos ([0-9_]+)/);
        if (match) osVersion = (match[1] || match[2]).replace(/_/g, '.');
      }
      // Android (el SO móvil más usado globalmente)
      else if (/android/.test(userAgent)) {
        os = 'Android';
        const match = userAgent.match(/android ([0-9\.]+)/);
        if (match) osVersion = match[1];
      }
      // iOS
      else if (/iphone|ipad|ipod/.test(userAgent)) {
        os = 'iOS';
        const match = userAgent.match(/os ([0-9_]+)/);
        if (match) osVersion = match[1].replace(/_/g, '.');
      }
      // Linux (distribuciones principales)
      else if (/linux/.test(userAgent)) {
        os = 'Linux';
        const distros = {
          ubuntu: 'Ubuntu',
          fedora: 'Fedora',
          debian: 'Debian',
          'red hat': 'Red Hat',
          centos: 'CentOS',
          suse: 'SUSE',
          mint: 'Linux Mint',
          arch: 'Arch Linux',
          gentoo: 'Gentoo',
        };
        for (const [key, value] of Object.entries(distros)) {
          if (userAgent.includes(key)) {
            osVersion = value;
            break;
          }
        }
      }
      // Chrome OS
      else if (/cros/.test(userAgent)) {
        os = 'Chrome OS';
      }
      // Unix/BSD
      else if (/x11|unix|bsd/.test(userAgent)) {
        os = 'Unix';
        if (/freebsd/.test(userAgent)) osVersion = 'FreeBSD';
        else if (/openbsd/.test(userAgent)) osVersion = 'OpenBSD';
        else if (/netbsd/.test(userAgent)) osVersion = 'NetBSD';
      }
      // Sistemas móviles menos comunes
      else if (/windows phone/.test(userAgent)) {
        os = 'Windows Phone';
      } else if (/blackberry/.test(userAgent)) {
        os = 'BlackBerry';
      } else if (/symbian/.test(userAgent)) {
        os = 'Symbian';
      } else if (/webos/.test(userAgent)) {
        os = 'webOS';
      } else if (/kaios/.test(userAgent)) {
        os = 'KaiOS';
      }

      // Detección de Navegadores (Global - por cuota de mercado)
      let browser = 'Unknown';
      let browserVersion = null;

      // Navegadores basados en Chromium (los más usados)
      const chromeMatch = userAgent.match(/chrome\/([0-9\.]+)/);

      // Brave
      if (navigator.brave !== undefined) {
        browser = 'Brave';
        browserVersion = chromeMatch ? chromeMatch[1] : null;
      }
      // Microsoft Edge (Chromium)
      else if (/edg\//.test(userAgent)) {
        browser = 'Microsoft Edge';
        const match = userAgent.match(/edg\/([0-9\.]+)/);
        browserVersion = match ? match[1] : null;
      }
      // Opera
      else if (/opera|opr\//.test(userAgent)) {
        browser = 'Opera';
        const match = userAgent.match(/(opera|opr)\/([0-9\.]+)/);
        browserVersion = match ? match[2] : chromeMatch?.[1] || null;
      }
      // Chrome
      else if (chromeMatch && !/edg\//.test(userAgent)) {
        browser = 'Google Chrome';
        browserVersion = chromeMatch[1];
      }
      // Firefox
      else if (/firefox/.test(userAgent)) {
        browser = 'Mozilla Firefox';
        const match = userAgent.match(/firefox\/([0-9\.]+)/);
        browserVersion = match ? match[1] : null;
      }
      // Safari
      else if (/safari/.test(userAgent) && !/chrome/.test(userAgent)) {
        browser = 'Safari';
        const match = userAgent.match(/version\/([0-9\.]+)/);
        browserVersion = match ? match[1] : null;
      }
      // Internet Explorer (legacy)
      else if (/msie|trident/.test(userAgent)) {
        browser = 'Internet Explorer';
        const match = userAgent.match(/(msie |rv:)([0-9\.]+)/);
        browserVersion = match ? match[2] : null;
      }
      // Samsung Internet
      else if (/samsungbrowser/.test(userAgent)) {
        browser = 'Samsung Internet';
        const match = userAgent.match(/samsungbrowser\/([0-9\.]+)/);
        browserVersion = match ? match[1] : null;
      }
      // UC Browser (Asia)
      else if (/ucbrowser/.test(userAgent)) {
        browser = 'UC Browser';
        const match = userAgent.match(/ucbrowser\/([0-9\.]+)/);
        browserVersion = match ? match[1] : null;
      }
      // QQ Browser (China)
      else if (/qqbrowser/.test(userAgent)) {
        browser = 'QQ Browser';
        const match = userAgent.match(/qqbrowser\/([0-9\.]+)/);
        browserVersion = match ? match[1] : null;
      }
      // Baidu Browser (China)
      else if (/baidubrowser/.test(userAgent)) {
        browser = 'Baidu Browser';
        const match = userAgent.match(/baidubrowser\/([0-9\.]+)/);
        browserVersion = match ? match[1] : null;
      }
      // Yandex Browser (Rusia)
      else if (/yabrowser/.test(userAgent)) {
        browser = 'Yandex Browser';
        const match = userAgent.match(/yabrowser\/([0-9\.]+)/);
        browserVersion = match ? match[1] : null;
      }

      // Detección de Fabricantes de Dispositivos (Global)
      let deviceVendor = 'Unknown';
      let deviceModel = 'Unknown';

      // Apple (Global)
      if (/iphone/.test(userAgent)) {
        deviceVendor = 'Apple';
        deviceModel = 'iPhone';
        // Detectar modelo específico de iPhone
        const modelMatch = userAgent.match(/iphone\s+([a-z0-9]+)/i);
        if (modelMatch) deviceModel = `iPhone ${modelMatch[1].toUpperCase()}`;
      } else if (/ipad/.test(userAgent)) {
        deviceVendor = 'Apple';
        deviceModel = 'iPad';
      } else if (/macintosh/.test(userAgent)) {
        deviceVendor = 'Apple';
        deviceModel = 'Mac';
      } else if (/ipod/.test(userAgent)) {
        deviceVendor = 'Apple';
        deviceModel = 'iPod';
      }
      // Samsung (Corea del Sur)
      else if (/samsung/.test(userAgent)) {
        deviceVendor = 'Samsung';
        deviceModel = 'Galaxy Phone';
        const modelMatch = userAgent.match(/sm-([a-z0-9]+)/i);
        if (modelMatch) deviceModel = `Galaxy ${modelMatch[1].toUpperCase()}`;
      }
      // Xiaomi (China)
      else if (/mi\s|redmi|xiaomi/.test(userAgent)) {
        deviceVendor = 'Xiaomi';
        const modelMatch = userAgent.match(
          /(mi\s|redmi|xiaomi)\s*([a-z0-9\s]+)/i,
        );
        deviceModel = modelMatch
          ? `${modelMatch[1]} ${modelMatch[2]}`.trim()
          : 'Xiaomi Phone';
      }
      // Huawei (China)
      else if (/huawei|honor/.test(userAgent)) {
        deviceVendor = 'Huawei';
        deviceModel = 'Huawei Phone';
      }
      // OPPO (China)
      else if (/oppo/.test(userAgent)) {
        deviceVendor = 'OPPO';
        deviceModel = 'OPPO Phone';
      }
      // Vivo (China)
      else if (/vivo/.test(userAgent)) {
        deviceVendor = 'Vivo';
        deviceModel = 'Vivo Phone';
      }
      // Realme (China)
      else if (/realme/.test(userAgent)) {
        deviceVendor = 'Realme';
        deviceModel = 'Realme Phone';
      }
      // OnePlus (China)
      else if (/oneplus/.test(userAgent)) {
        deviceVendor = 'OnePlus';
        deviceModel = 'OnePlus Phone';
      }
      // Google (Global)
      else if (/pixel/.test(userAgent)) {
        deviceVendor = 'Google';
        deviceModel = 'Pixel';
      }
      // Sony (Japón)
      else if (/sony/.test(userAgent)) {
        deviceVendor = 'Sony';
        deviceModel = 'Xperia';
      }
      // LG (Corea del Sur)
      else if (/lg/.test(userAgent)) {
        deviceVendor = 'LG';
        deviceModel = 'LG Phone';
      }
      // Nokia (Finlandia)
      else if (/nokia/.test(userAgent)) {
        deviceVendor = 'Nokia';
        deviceModel = 'Nokia Phone';
      }
      // Motorola (USA)
      else if (/motorola/.test(userAgent)) {
        deviceVendor = 'Motorola';
        deviceModel = 'Moto';
      }
      // Lenovo (China)
      else if (/lenovo/.test(userAgent)) {
        deviceVendor = 'Lenovo';
        deviceModel = 'Lenovo Device';
      }
      // Amazon (USA)
      else if (/kindle/.test(userAgent)) {
        deviceVendor = 'Amazon';
        deviceModel = 'Kindle';
      }

      // Características adicionales
      const isTouchDevice =
        'ontouchstart' in window || navigator.maxTouchPoints > 0;
      const language = navigator.language || navigator.userLanguage || 'en';
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      // Determinar tipo de dispositivo principal
      let deviceType = 'desktop';
      if (isTablet) {
        deviceType = 'tablet';
      } else if (isMobile) {
        deviceType = 'mobile';
      }

      return {
        isMobile,
        isTablet,
        isDesktop: !isMobile && !isTablet,
        os,
        osVersion,
        browser,
        browserVersion,
        deviceType,
        deviceVendor,
        deviceModel,
        isTouchDevice,
        language,
        timezone,
        userAgent: navigator.userAgent,
        platform: navigator.platform,
        cookieEnabled: navigator.cookieEnabled,
        javaEnabled: navigator.javaEnabled && navigator.javaEnabled(),
        pdfViewerEnabled: navigator.pdfViewerEnabled || false,
      };
    };

    setDeviceInfo(detectDevice());
  }, []);

  return deviceInfo;
};
